import React, { useState, useEffect, useCallback } from 'react';
import { Col, Dropdown, Row, Button } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import SimpleBar from "simplebar-react";
import { useTranslation } from 'react-i18next';
import { dashboardAPI } from '../services/api';

interface Notification {
    id: string;
    type: string;
    title: string;
    message: string;
    status: string;
    alertId?: string;
    transactionId?: string;
    createdOn: string;
}

const NotificationDropdown = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [notifications, setNotifications] = useState<Notification[]>([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [showDropdown, setShowDropdown] = useState(false);

    const fetchNotifications = useCallback(async () => {
        setIsLoading(true);
        try {
            const response = await dashboardAPI.getNotifications({ page: 0, size: 10 });
            setNotifications(response.content || []);
        } catch (error) {
            console.error('Failed to fetch notifications:', error);
        } finally {
            setIsLoading(false);
        }
    }, []);

    const fetchUnreadCount = useCallback(async () => {
        try {
            const response = await dashboardAPI.getUnreadNotificationCount();
            setUnreadCount(response.count || 0);
        } catch (error) {
            console.error('Failed to fetch unread count:', error);
        }
    }, []);

    useEffect(() => {
        fetchNotifications();
        fetchUnreadCount();
        
        // Refresh notifications every 30 seconds
        const interval = setInterval(() => {
            fetchNotifications();
            fetchUnreadCount();
        }, 30000);

        return () => clearInterval(interval);
    }, [fetchNotifications, fetchUnreadCount]);

    const handleNotificationClick = async (notification: Notification) => {
        // Mark as read if unread
        if (notification.status === 'UNREAD') {
            try {
                await dashboardAPI.markNotificationAsRead(notification.id);
                // Update local state
                setNotifications(prev => prev.map(n => 
                    n.id === notification.id ? { ...n, status: 'READ' } : n
                ));
                setUnreadCount(prev => Math.max(0, prev - 1));
            } catch (error) {
                console.error('Failed to mark notification as read:', error);
            }
        }

        // Navigate to alert if it's an alert notification
        if (notification.type === 'ALERT' && notification.alertId) {
            navigate(`/alert/${notification.alertId}`);
            setShowDropdown(false);
        }
    };

    const handleMarkAllAsRead = async () => {
        try {
            await dashboardAPI.markAllNotificationsAsRead();
            setNotifications(prev => prev.map(n => ({ ...n, status: 'READ' })));
            setUnreadCount(0);
        } catch (error) {
            console.error('Failed to mark all as read:', error);
        }
    };

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        const now = new Date();
        const diffMs = now.getTime() - date.getTime();
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        if (diffMins < 1) return t('Notifications.just_now') || 'Just now';
        if (diffMins < 60) return `${diffMins} ${t('Notifications.minutes_ago') || 'minutes ago'}`;
        if (diffHours < 24) return `${diffHours} ${t('Notifications.hours_ago') || 'hours ago'}`;
        if (diffDays < 7) return `${diffDays} ${t('Notifications.days_ago') || 'days ago'}`;
        return date.toLocaleDateString('tr-TR');
    };

    const getNotificationIcon = (type: string) => {
        switch (type) {
            case 'ALERT':
                return 'bx bx-bell';
            case 'TRANSACTION':
                return 'bx bx-receipt';
            case 'SYSTEM':
                return 'bx bx-info-circle';
            default:
                return 'bx bx-bell';
        }
    };

    const getNotificationColor = (type: string) => {
        switch (type) {
            case 'ALERT':
                return 'bg-danger-subtle text-danger';
            case 'TRANSACTION':
                return 'bg-info-subtle text-info';
            case 'SYSTEM':
                return 'bg-primary-subtle text-primary';
            default:
                return 'bg-secondary-subtle text-secondary';
        }
    };

    return (
        <React.Fragment>
            <Dropdown 
                className="dropdown topbar-head-dropdown ms-1 header-item" 
                id="notificationDropdown"
                show={showDropdown}
                onToggle={setShowDropdown}
            >
                <Dropdown.Toggle 
                    id="notification" 
                    type="button" 
                    className="btn btn-icon btn-topbar btn-ghost-primary rounded-circle arrow-none"
                    onClick={() => setShowDropdown(!showDropdown)}
                >
                    <i className='las la-bell fs-24'></i>
                    {unreadCount > 0 && (
                        <span className="position-absolute topbar-badge fs-9 translate-middle badge rounded-pill bg-danger">
                            <span className="notification-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
                            <span className="visually-hidden">unread messages</span>
                        </span>
                    )}
                </Dropdown.Toggle>
                <Dropdown.Menu 
                    className="dropdown-menu-lg dropdown-menu-end p-0" 
                    aria-labelledby="page-header-notifications-dropdown"
                >
                    <div className="dropdown-head rounded-top">
                        <div className="p-3 bg-primary bg-pattern">
                            <Row className="align-items-center">
                                <Col>
                                    <h6 className="m-0 fs-16 fw-semibold text-white">
                                        {t('Notifications.title') || 'Notifications'}
                                    </h6>
                                </Col>
                                <div className="col-auto dropdown-tabs">
                                    {unreadCount > 0 && (
                                        <span className="badge bg-light-subtle text-light fs-13">
                                            {unreadCount} {t('Notifications.unread') || 'unread'}
                                        </span>
                                    )}
                                </div>
                            </Row>
                        </div>

                        <div className="pt-2">
                            <SimpleBar style={{ maxHeight: "300px" }} className="pe-2">
                                {isLoading ? (
                                    <div className="text-center py-3">
                                        <div className="spinner-border spinner-border-sm text-primary" role="status">
                                            <span className="visually-hidden">Loading...</span>
                                        </div>
                                    </div>
                                ) : notifications.length === 0 ? (
                                    <div className="text-center py-4 text-muted">
                                        <i className="las la-bell-slash fs-24 mb-2"></i>
                                        <p className="mb-0">{t('Notifications.no_notifications') || 'No notifications'}</p>
                                    </div>
                                ) : (
                                    <>
                                        {notifications.map((notification) => (
                                            <div
                                                key={notification.id}
                                                className={`text-reset notification-item d-block dropdown-item position-relative ${notification.status === 'UNREAD' ? 'bg-light' : ''}`}
                                                onClick={() => handleNotificationClick(notification)}
                                                style={{ cursor: 'pointer' }}
                                            >
                                                <div className="d-flex">
                                                    <div className="avatar-xs me-3">
                                                        <span className={`avatar-title rounded-circle fs-16 ${getNotificationColor(notification.type)}`}>
                                                            <i className={getNotificationIcon(notification.type)}></i>
                                                        </span>
                                                    </div>
                                                    <div className="flex-1">
                                                        <h6 className={`mt-0 mb-1 fs-13 lh-base ${notification.status === 'UNREAD' ? 'fw-semibold' : ''}`}>
                                                            {notification.title}
                                                        </h6>
                                                        <p className="mb-0 fs-12 text-muted" style={{
                                                            display: '-webkit-box',
                                                            WebkitLineClamp: 2,
                                                            WebkitBoxOrient: 'vertical',
                                                            overflow: 'hidden'
                                                        }}>
                                                            {notification.message}
                                                        </p>
                                                        <p className="mb-0 fs-11 fw-medium text-uppercase text-muted mt-1">
                                                            <span><i className="mdi mdi-clock-outline"></i> {formatDate(notification.createdOn)}</span>
                                                        </p>
                                                    </div>
                                                    {notification.status === 'UNREAD' && (
                                                        <div className="ms-2">
                                                            <span className="badge bg-primary rounded-circle" style={{ width: '8px', height: '8px' }}></span>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </>
                                )}

                                {notifications.length > 0 && (
                                    <div className="my-3 text-center view-all border-top pt-3">
                                        <div className="d-flex gap-2 justify-content-center">
                                            {unreadCount > 0 && (
                                                <Button
                                                    variant="soft-primary"
                                                    size="sm"
                                                    onClick={handleMarkAllAsRead}
                                                >
                                                    {t('Notifications.mark_all_read') || 'Mark all as read'}
                                                </Button>
                                            )}
                                            <Link
                                                to="/alerts"
                                                className="btn btn-soft-success btn-sm waves-effect waves-light"
                                                onClick={() => setShowDropdown(false)}
                                            >
                                                {t('Notifications.view_all') || 'View All'}
                                                <i className="ri-arrow-right-line align-middle ms-1"></i>
                                            </Link>
                                        </div>
                                    </div>
                                )}
                            </SimpleBar>
                        </div>
                    </div>
                </Dropdown.Menu>
            </Dropdown>
        </React.Fragment>
    );
};

export default NotificationDropdown;
